import { useKestrel } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { rooftopDamageCategories } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/damageTypes/rooftopDamageTypes'
import { DamageTypesSelect } from 'features/kestrelViewer/ui/tools/dataValidation/components/DamageTypesSelect'
import { NotesInput } from 'features/kestrelViewer/ui/tools/dataValidation/components/NotesInput'
import { PhotosList } from 'features/kestrelViewer/ui/tools/dataValidation/components/PhotosList'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { KVAnnotation, RooftopDamageId } from 'types'

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  overflow: hidden;
`

const ContentRow = styled.div`
  display: flex;
  gap: 1rem;
  flex: 1;
  overflow: hidden;
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
`

const NotesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-height: 0;
`

interface DataValidationPanelContentProps {
  annotations: KVAnnotation[]
  currentIndex: number
  onAnnotationLoad?: (annotation: KVAnnotation) => void
  kestrelModelId?: string
  currentUnit: 'Meters' | 'Feet' | 'Inches' | 'Millimeters'
  soundEnabled: boolean
  severity: string
  onSeverityChange: (severity: string) => void
  onNotesChange: (annotationId: string, notes: string) => void
  currentNotes: string
  onDamageTypesChange: (
    annotationId: string,
    damageTypes: RooftopDamageId[]
  ) => void
  currentDamageTypes: RooftopDamageId[]
}

export const DataValidationPanelContent: React.FC<
  DataValidationPanelContentProps
> = ({
  annotations,
  currentIndex,
  onAnnotationLoad,
  kestrelModelId,
  onSeverityChange,
  onNotesChange,
  currentNotes,
  onDamageTypesChange,
  currentDamageTypes,
}) => {
  const currentAnnotation = annotations[currentIndex]

  const { sendKVCommand } = useKestrel()

  // Initialize severity when annotation changes
  useEffect(() => {
    if (currentAnnotation) {
      onSeverityChange(currentAnnotation.severity || '')
      onAnnotationLoad?.(currentAnnotation)
    }
  }, [currentAnnotation, onAnnotationLoad, onSeverityChange])

  // Highlight current annotation in the viewer
  useEffect(() => {
    if (!currentAnnotation || !sendKVCommand) return

    if (currentAnnotation.geometry?.kind === 'polygon') {
      sendKVCommand({
        command: KestrelCommands.HIGHLIGHT_SURFACE_AREA,
        surfaceAreaId: currentAnnotation.id,
      })
    } else {
      sendKVCommand({
        command: KestrelCommands.HIGHLIGHT_ANNOTATION,
        annotationId: currentAnnotation.id,
      })
    }

    return () => {
      if (!sendKVCommand) return

      if (currentAnnotation.geometry?.kind === 'polygon') {
        sendKVCommand({
          command: KestrelCommands.UNHIGHLIGHT_ALL_SURFACE_AREAS,
        })
      } else {
        sendKVCommand({
          command: KestrelCommands.UNHIGHLIGHT_ALL_ANNOTATIONS,
        })
      }
    }
  }, [currentAnnotation, sendKVCommand])

  // List all files under the annotation's Screenshots directory
  const screenshotQuery = useMemo(() => {
    if (!currentAnnotation?.id || !kestrelModelId) return undefined
    return {
      entry_id: kestrelModelId,
      path: `Screenshots/${currentAnnotation.id}/**`,
    }
  }, [currentAnnotation?.id, kestrelModelId])

  const { fileRefs } = useRolodexFileLS(screenshotQuery)

  // Filter for image files only
  const imageFileQueries = useMemo(() => {
    return fileRefs
      .filter((file) => {
        const lowerPath = file.path.toLowerCase()
        return (
          lowerPath.endsWith('.png') ||
          lowerPath.endsWith('.jpg') ||
          lowerPath.endsWith('.jpeg')
        )
      })
      .map((file) => ({
        entry_id: file.entry_id,
        path: file.path,
      }))
  }, [fileRefs])

  const { results: signedUrlResults } = useRolodexSignedURLs(
    imageFileQueries.length > 0 ? imageFileQueries : []
  )

  const signedUrls = useMemo(
    () => signedUrlResults.map((result) => result.signed_url).filter(Boolean),
    [signedUrlResults]
  )

  const handleAddDamageTypes = (value: unknown) => {
    if (!currentAnnotation) return
    const selectedArray = Array.isArray(value)
      ? (value as RooftopDamageId[])
      : []
    onDamageTypesChange(currentAnnotation.id, selectedArray)
  }

  // Grouped options for the dropdown
  const groupedOptions = useMemo(
    () =>
      rooftopDamageCategories.map((category) => ({
        label: category.label,
        options: category.types.map((type) => ({
          label: type.label,
          value: type.id,
        })),
      })),
    []
  )

  const handleNotesChange = (notes: string) => {
    if (currentAnnotation) {
      onNotesChange(currentAnnotation.id, notes)
    }
  }

  return (
    <PanelContent>
      <ContentRow>
        <LeftColumn>
          <PhotosList signedUrls={signedUrls} />
        </LeftColumn>
        <RightColumn>
          <NotesSection>
            <NotesInput
              value={currentNotes}
              onChange={handleNotesChange}
              placeholder='Add notes for this annotation...'
            />
          </NotesSection>
          <DamageTypesSelect
            value={currentDamageTypes}
            onChange={handleAddDamageTypes}
            groupedOptions={groupedOptions}
            placeholder={
              currentDamageTypes.length > 0
                ? 'Add more damage types...'
                : 'Add damages'
            }
          />
        </RightColumn>
      </ContentRow>
    </PanelContent>
  )
}
